import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Eye, EyeOff, Trash2, AlertTriangle, X } from "lucide-react";
import api, { API, fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useProfileModal } from "@/contexts/ProfileModalContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

/**
 * GlobalProfileModal — mounted once at the app root. Opens via useProfileModal().
 * Replaces the old /profile page: same sections (Account / Password / Danger Zone)
 * but presented as a Dialog so the user keeps their place in the app.
 */
export default function GlobalProfileModal() {
  const { open, closeProfile } = useProfileModal();
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  // Lock background scroll + ESC-to-close while modal is open.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") closeProfile(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, closeProfile]);

  if (!open || !user) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start md:items-center justify-center p-4 md:p-8 bg-white/10 backdrop-blur-md"
      data-testid="profile-modal-backdrop"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => { if (e.target === e.currentTarget) closeProfile(); }}
    >
      <div
        className="relative w-full max-w-3xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-4rem)]"
        data-testid="profile-modal"
      >
        {/* Header */}
        <header className="shrink-0 px-6 md:px-8 pt-6 md:pt-8 pb-5 border-b border-gray-100 flex items-start justify-between gap-4 bg-white">
          <div className="min-w-0">
            <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-[#1a1a1a]">
              {t("profile_page.title")}
            </h2>
            <p className="text-sm text-gray-500 mt-1 max-w-xl">
              {t("profile_page.subtitle")}
            </p>
          </div>
          <button
            onClick={closeProfile}
            data-testid="profile-modal-close"
            aria-label={t("profile_page.cancel")}
            className="shrink-0 w-9 h-9 rounded-md flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </header>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6 bg-[#fafafa]" data-testid="profile-modal-body">
          <div className="space-y-6">
            <ProfileForm />
            <PasswordForm />
            <DangerZone onDeleted={() => { closeProfile(); logout?.(); navigate("/login", { replace: true }); }} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Profile form ---------- */
function ProfileForm() {
  const { user, updateProfile, refresh } = useAuth();
  const { t } = useI18n();
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [saving, setSaving] = useState(false);

  const base = useMemo(() => ({
    first_name: user?.first_name || (user?.name ? user.name.split(" ")[0] : ""),
    last_name: user?.last_name || (user?.name ? user.name.split(" ").slice(1).join(" ") : ""),
    email: user?.email || "",
  }), [user?.first_name, user?.last_name, user?.name, user?.email]);
  const [form, setForm] = useState(base);
  const dirty = form.first_name !== base.first_name || form.last_name !== base.last_name;

  useEffect(() => { setForm(base); }, [user?.user_id, base]);

  const save = async () => {
    setSaving(true);
    try {
      await updateProfile({
        first_name: form.first_name,
        last_name: form.last_name,
        name: [form.first_name, form.last_name].filter(Boolean).join(" "),
      });
      toast.success(t("profile_page.saved"));
    } catch { toast.error(t("profile_page.save_failed")); }
    finally { setSaving(false); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg)$/.test(file.type)) { toast.error(t("profile_page.photo_invalid_type")); return; }
    setUploading(true);
    try {
      const token = localStorage.getItem("bh_token");
      const fd = new FormData();
      fd.append("file", file);
      const resp = await fetch(`${API}/users/me/avatar`, { method: "POST", body: fd, headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
      if (!resp.ok) throw new Error("upload failed");
      await refresh();
      setShowUploader(false);
      toast.success(t("profile_page.photo_uploaded"));
    } catch { toast.error(t("profile_page.photo_upload_failed")); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const avatarUrl = user?.avatar_file_id ? fileUrl(user.avatar_file_id) : user?.picture;
  const hasAvatar = !!avatarUrl;

  return (
    <Section
      title={t("profile_page.account_title")}
      subtitle={t("profile_page.account_subtitle")}
      action={<SaveButton onClick={save} testid="profile-save-global" disabled={saving} dirty={dirty} label={t("profile_page.save_changes")} />}
    >
      <div>
        <Label className="text-[13px] text-[#1a1a1a]">{t("profile_page.photo_label")}</Label>
        <div className="mt-2 flex items-stretch gap-3">
          <div
            className="w-24 h-24 rounded-md shrink-0 overflow-hidden"
            style={{ background: avatarUrl ? `center/cover no-repeat url('${avatarUrl}')` : "linear-gradient(135deg,#ff8ab1 0%,#fecbc6 20%,#fef067 45%,#60d394 70%,#4cc9f0 100%)" }}
            data-testid="profile-photo-preview-global"
          />
          {hasAvatar && !showUploader ? (
            <div className="flex items-center">
              <Button
                onClick={() => setShowUploader(true)}
                data-testid="profile-choose-another-global"
                className="h-10 rounded-md bg-white border border-gray-200 text-slate-900 hover:bg-gray-50 shadow-none text-sm"
              >
                <Upload className="w-4 h-4 mr-2" /> {t("profile_page.photo_choose_another")}
              </Button>
            </div>
          ) : (
            <div
              role="button" tabIndex={0}
              onClick={() => !uploading && fileRef.current?.click()}
              onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !uploading) { e.preventDefault(); fileRef.current?.click(); } }}
              onDragOver={(e) => { e.preventDefault(); if (!uploading) setIsDragging(true); }}
              onDragEnter={(e) => { e.preventDefault(); if (!uploading) setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => { e.preventDefault(); setIsDragging(false); uploadFile(e.dataTransfer?.files?.[0]); }}
              data-testid="profile-photo-upload-zone-global"
              className={`flex-1 min-w-0 flex items-center justify-center gap-2 rounded-md border border-dashed px-4 cursor-pointer transition ${uploading ? "opacity-60 cursor-wait" : ""} ${isDragging ? "border-slate-900 bg-gray-100" : "border-gray-300 bg-gray-50 hover:bg-gray-100"}`}
            >
              <Upload className="w-4 h-4 text-gray-500 pointer-events-none" />
              <div className="text-sm text-[#1a1a1a] pointer-events-none">{isDragging ? t("profile_page.photo_drop_release") : t("profile_page.photo_drop_prompt")}</div>
              <div className="text-xs text-gray-500 pointer-events-none">{t("profile_page.photo_hint")}</div>
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 pt-4">
        <div>
          <Label className="text-[13px] text-[#1a1a1a]">{t("settings.profile_first_name")}</Label>
          <Input data-testid="profile-first-name-global" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="John" className="mt-1 h-11 bg-white border-gray-200" />
        </div>
        <div>
          <Label className="text-[13px] text-[#1a1a1a]">{t("settings.profile_last_name")}</Label>
          <Input data-testid="profile-last-name-global" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Doe" className="mt-1 h-11 bg-white border-gray-200" />
        </div>
      </div>
      <div>
        <Label className="text-[13px] text-[#1a1a1a]">{t("settings.profile_email")}</Label>
        <Input data-testid="profile-email-global" value={form.email} disabled className="mt-1 h-11 bg-gray-50 border-gray-200 cursor-not-allowed" />
      </div>
    </Section>
  );
}

/* ---------- Password form ---------- */
function PasswordForm() {
  const { t } = useI18n();
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const dirty = !!(pwd.current || pwd.next || pwd.confirm);

  const save = async () => {
    if (!pwd.current) { toast.error(t("toasts.password_current_required")); return; }
    if (!pwd.next || pwd.next.length < 8) { toast.error(t("toasts.password_too_short")); return; }
    if (pwd.next !== pwd.confirm) { toast.error(t("toasts.passwords_mismatch")); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success(t("toasts.password_updated"));
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || t("profile_page.save_failed")); }
    finally { setSaving(false); }
  };

  return (
    <Section
      title={t("profile_page.change_password_title")}
      subtitle={t("profile_page.change_password_subtitle")}
      action={<SaveButton onClick={save} testid="password-save-global" disabled={saving} dirty={dirty} label={t("profile_page.update_password")} />}
    >
      <PasswordField label={t("profile_page.pw_current")} value={pwd.current} onChange={(v) => setPwd({ ...pwd, current: v })} show={show.current} onToggle={() => setShow((s) => ({ ...s, current: !s.current }))} testid="password-current-global" />
      <PasswordField label={t("profile_page.pw_new")} value={pwd.next} onChange={(v) => setPwd({ ...pwd, next: v })} show={show.next} onToggle={() => setShow((s) => ({ ...s, next: !s.next }))} testid="password-new-global" />
      <PasswordField label={t("profile_page.pw_confirm")} value={pwd.confirm} onChange={(v) => setPwd({ ...pwd, confirm: v })} show={show.confirm} onToggle={() => setShow((s) => ({ ...s, confirm: !s.confirm }))} testid="password-confirm-global" />
    </Section>
  );
}

function PasswordField({ label, value, onChange, show, onToggle, testid }) {
  const { t } = useI18n();
  return (
    <div>
      <Label className="text-[13px] text-[#1a1a1a]">{label}</Label>
      <div className="relative mt-1">
        <Input
          data-testid={testid}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="••••••••"
          className="h-11 bg-white border-gray-200 pr-10"
        />
        <button
          type="button"
          data-testid={`${testid}-toggle`}
          onClick={onToggle}
          className="absolute inset-y-0 right-2 flex items-center justify-center w-7 text-gray-400 hover:text-[#1a1a1a]"
          aria-label={show ? t("settings.hide_password") : t("settings.show_password")}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

/* ---------- Danger zone ---------- */
function DangerZone({ onDeleted }) {
  const { t } = useI18n();
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwd, setPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const confirmDelete = async () => {
    setSubmitting(true);
    try {
      await api.post("/auth/delete-account", { current_password: pwd || undefined });
      toast.success(t("profile_page.deleted"));
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("profile_page.delete_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="rounded-xl border border-red-200 bg-white p-6 md:p-8"
      data-testid="danger-zone"
    >
      <header className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-red-600" strokeWidth={2} />
            <h2 className="font-display text-2xl font-medium text-red-700">{t("profile_page.danger_zone")}</h2>
          </div>
          <p className="text-sm text-gray-500">
            {t("profile_page.danger_zone_sub")}
          </p>
        </div>
      </header>

      {!showConfirm ? (
        <Button
          onClick={() => setShowConfirm(true)}
          data-testid="delete-account-btn"
          className="h-10 rounded-md bg-red-600 text-white hover:bg-red-700"
        >
          <Trash2 className="w-4 h-4 mr-2" /> {t("profile_page.delete_account")}
        </Button>
      ) : (
        <div className="rounded-lg bg-red-50 border border-red-200 p-5 space-y-4" data-testid="delete-account-confirm">
          <div className="text-sm text-red-800">
            {t("profile_page.delete_confirm_body")}
          </div>
          <div>
            <Label className="text-[13px] text-red-900">{t("profile_page.pw_delete_label")}</Label>
            <Input
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              data-testid="delete-account-password"
              className="mt-1 h-11 bg-white border-red-200"
              placeholder="••••••••"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => { setShowConfirm(false); setPwd(""); }}
              className="h-10 rounded-md bg-white border border-gray-200 text-slate-900 hover:bg-gray-50 shadow-none"
              data-testid="delete-account-cancel"
            >
              {t("profile_page.cancel")}
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={submitting}
              data-testid="delete-account-confirm-btn"
              className="h-10 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {submitting ? t("profile_page.deleting") : t("profile_page.confirm_delete")}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------- Shared primitives ---------- */
function Section({ title, subtitle, action, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-6 md:p-7">
      <header className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h2 className="font-display text-xl font-medium text-[#1a1a1a]">{title}</h2>
          {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </header>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

function SaveButton({ onClick, testid, disabled, dirty, label }) {
  const inactive = disabled || !dirty;
  return (
    <Button
      onClick={onClick}
      disabled={inactive}
      data-testid={testid}
      className={`h-10 rounded-md shrink-0 px-4 transition-colors ${
        inactive
          ? "bg-gray-100 text-gray-400 hover:bg-gray-100 cursor-not-allowed"
          : "bg-slate-900 text-white hover:bg-slate-800"
      }`}
    >
      {label}
    </Button>
  );
}
